/**
 * DevicesTab Component
 *
 * Tab for viewing and managing user push notification devices.
 * Features: view device list, delete devices, activate/deactivate devices.
 */

import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { IconDevices, IconTrash, IconRefresh, IconPlayerPause, IconPlayerPlay, IconAlertCircle } from '@tabler/icons-react';
import {
  Card,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Alert,
  ScrollArea,
  Modal,
  Text,
  EmptyState,
} from '../../../components/ui';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../config/ccms';
import type { DevicesTabProps, ResponseData } from './userTabTypes';

export function DevicesTab({
  userId,
  devices,
  onDeviceDelete,
  onDeviceToggle,
  onRefresh,
  loading = false,
}: DevicesTabProps) {
  const httpClient = useHttpClient();
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<string | null>(null);

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp: number | null | undefined) => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp * 1000); // Assuming Unix timestamp
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Handle delete device confirmation
   */
  const handleDeleteDevice = (deviceId: string) => {
    setDeviceToDelete(deviceId);
    setDeleteConfirmOpened(true);
  };

  /**
   * Confirm delete device
   */
  const confirmDeleteDevice = async () => {
    if (!deviceToDelete) return;

    setActionLoading(true);
    setError(null);

    try {
      const url = getApiUrl('users', `/api/userpushdevice/${deviceToDelete}`);
      await httpClient.delete<ResponseData<boolean>>(url);

      notifications.show({
        title: 'Success',
        message: 'Device deleted successfully',
        color: 'green',
      });

      setDeleteConfirmOpened(false);
      setDeviceToDelete(null);
      await onDeviceDelete(deviceToDelete);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete device';
      setError(errorMessage);
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle toggle device active status
   */
  const handleToggleDevice = async (deviceId: string, activate: boolean) => {
    setActionLoading(true);
    setError(null);

    try {
      const endpoint = activate ? 'activate' : 'deactivate';
      const url = getApiUrl('users', `/api/userpushdevice/${deviceId}/${endpoint}`);
      await httpClient.post<ResponseData<boolean>>(url, {});

      notifications.show({
        title: 'Success',
        message: `Device ${activate ? 'activated' : 'deactivated'} successfully`,
        color: 'green',
      });

      await onDeviceToggle(deviceId, activate);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to toggle device';
      setError(errorMessage);
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle refresh devices
   */
  const handleRefresh = async () => {
    setError(null);
    try {
      await onRefresh();
      notifications.show({
        title: 'Refreshed',
        message: 'Device list refreshed',
        color: 'blue',
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to refresh devices';
      setError(errorMessage);
    }
  };

  return (
    <Stack gap="md">
      {/* Error Alert */}
      {error && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="Error"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Devices Card */}
      <Card withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                Registered Devices
              </Text>
              <Text size="xs" c="dimmed">
                Push notification devices registered by this user
              </Text>
            </div>
            <Group gap="xs">
              <Badge size="lg" variant="light" color="blue">
                {devices.length} {devices.length === 1 ? 'Device' : 'Devices'}
              </Badge>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={loading}
                size="sm"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {/* Devices Table */}
          {devices.length > 0 ? (
            <ScrollArea>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Device Name</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Platform</Table.Th>
                    <Table.Th>App Version</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Last Updated</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {devices.map((device) => (
                    <Table.Tr key={device.id}>
                      <Table.Td>
                        <Text fw={500}>{device.deviceName || 'Unknown Device'}</Text>
                        <Text size="xs" c="dimmed">
                          {device.deviceId || 'No ID'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{device.deviceType || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{device.platform || '-'}</Text>
                        {device.osVersion && (
                          <Text size="xs" c="dimmed">
                            {device.osVersion}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{device.appVersion || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={device.isActive ? 'green' : 'gray'} variant="light">
                          {device.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {formatTimestamp(device.lastUpdated)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <ActionIcon
                            variant="light"
                            color={device.isActive ? 'orange' : 'green'}
                            onClick={() => handleToggleDevice(device.id, !device.isActive)}
                            disabled={actionLoading}
                            title={device.isActive ? 'Deactivate device' : 'Activate device'}
                          >
                            {device.isActive ? (
                              <IconPlayerPause size={16} />
                            ) : (
                              <IconPlayerPlay size={16} />
                            )}
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDeleteDevice(device.id)}
                            disabled={actionLoading}
                            title="Delete device"
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          ) : (
            <EmptyState
              icon={<IconDevices size={64} />}
              title="No devices registered"
              description="This user has not registered any push notification devices."
              size="md"
            />
          )}
        </Stack>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteConfirmOpened}
        onClose={() => setDeleteConfirmOpened(false)}
        title="Confirm Delete Device"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this device? The user will no longer receive push
            notifications on this device.
          </Text>

          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setDeleteConfirmOpened(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDeleteDevice} loading={actionLoading}>
              Delete Device
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
